(() => {
  const ROOT_SELECTOR = '[data-mm-testimonials-carousel]';
  const MOBILE_BREAKPOINT = 767;
  const TABLET_BREAKPOINT = 1199;
  const SWIPE_THRESHOLD = 40;

  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  const initTestimonialsCarousel = (root) => {
    if (!root || root.dataset.mmTestimonialsInitialized === 'true') return;
    root.dataset.mmTestimonialsInitialized = 'true';

    const viewport = root.querySelector('.mm-testimonials-carousel__viewport');
    const track = root.querySelector('.mm-testimonials-carousel__track');
    const slides = Array.from(root.querySelectorAll('.mm-testimonials-carousel__slide'));
    const prevBtn = root.querySelector('.mm-testimonials-carousel__nav--prev');
    const nextBtn = root.querySelector('.mm-testimonials-carousel__nav--next');

    if (!viewport || !track || !slides.length) return;

    const mobileMode = root.dataset.mobileCardsView || 'one-two';
    const desktopCount = Number(root.dataset.desktopCardsView) || 3;
    const autoplayEnabled = root.dataset.autoplay === 'true';
    const autoplayDelay = Number.parseInt(root.dataset.autoplaySpeed || '5000', 10);

    let index = 0;
    let visibleCount = 1;
    let gap = 16;
    let slideStep = 0;
    let maxIndex = 0;
    let autoplayTimer = null;
    let isHovered = false;
    let isInView = false;
    let touchStartX = 0;
    let touchDiffX = 0;
    let rafId = 0;

    const getVisibleCount = () => {
      const width = window.innerWidth;

      if (width <= MOBILE_BREAKPOINT) {
        if (mobileMode === 'two') return 2;
        return 1;
      }

      if (width <= TABLET_BREAKPOINT) {
        return 2;
      }

      return desktopCount;
    };

    const getGap = () => (window.innerWidth <= MOBILE_BREAKPOINT ? 12 : 16);

    const stopAutoplay = () => {
      if (autoplayTimer) {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const applyTransform = () => {
      track.style.transform = `translate3d(-${index * slideStep}px, 0, 0)`;
    };

    const scheduleAutoplay = () => {
      stopAutoplay();

      if (
        !autoplayEnabled ||
        !isInView ||
        isHovered ||
        document.hidden ||
        slides.length <= visibleCount
      ) {
        return;
      }

      autoplayTimer = window.setTimeout(() => {
        next();
        scheduleAutoplay();
      }, autoplayDelay);
    };

    const recalc = () => {
      visibleCount = getVisibleCount();
      gap = getGap();

      const firstSlide = slides[0];
      if (!firstSlide) return;

      slideStep = firstSlide.offsetWidth + gap;
      maxIndex = Math.max(0, slides.length - visibleCount);
      index = clamp(index, 0, maxIndex);

      applyTransform();
      scheduleAutoplay();
    };

    const requestRecalc = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(recalc);
    };

    const next = () => {
      index = index >= maxIndex ? 0 : index + 1;
      applyTransform();
    };

    const prev = () => {
      index = index <= 0 ? maxIndex : index - 1;
      applyTransform();
    };

    const evaluateClamp = (textEl, button) => {
      if (!textEl || !button) return;

      if (textEl.classList.contains('is-expanded')) {
        button.classList.add('is-visible');
        return;
      }

      const isOverflowing = textEl.scrollHeight > textEl.clientHeight + 4;
      button.classList.toggle('is-visible', isOverflowing);
    };

    const collapsibleItems = Array.from(root.querySelectorAll('[data-collapsible-text]')).map((textEl) => {
      const button = textEl.parentElement?.querySelector('[data-toggle-text]');
      evaluateClamp(textEl, button);
      return { textEl, button };
    });

    const resizeObserver = new ResizeObserver(() => {
      requestRecalc();

      for (const item of collapsibleItems) {
        evaluateClamp(item.textEl, item.button);
      }
    });

    resizeObserver.observe(viewport);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        isInView = entry?.isIntersecting ?? false;

        if (isInView) {
          requestRecalc();
          scheduleAutoplay();
        } else {
          stopAutoplay();
        }
      },
      { threshold: 0.2 }
    );

    intersectionObserver.observe(root);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        scheduleAutoplay();
      }
    });

    prevBtn?.addEventListener('click', () => {
      prev();
      scheduleAutoplay();
    });

    nextBtn?.addEventListener('click', () => {
      next();
      scheduleAutoplay();
    });

    viewport.addEventListener('mouseenter', () => {
      isHovered = true;
      stopAutoplay();
    });

    viewport.addEventListener('mouseleave', () => {
      isHovered = false;
      scheduleAutoplay();
    });

    viewport.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.touches[0].clientX;
        touchDiffX = 0;
        stopAutoplay();
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchmove',
      (event) => {
        touchDiffX = event.touches[0].clientX - touchStartX;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      () => {
        if (Math.abs(touchDiffX) > SWIPE_THRESHOLD) {
          if (touchDiffX < 0) next();
          if (touchDiffX > 0) prev();
        }

        scheduleAutoplay();
      },
      { passive: true }
    );

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-toggle-text]');
      if (!toggle) return;

      const wrap = toggle.closest('.mm-testimonials-carousel__text-wrap');
      const textEl = wrap?.querySelector('[data-collapsible-text]');
      if (!textEl) return;

      const expanded = textEl.classList.toggle('is-expanded');
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.textContent = expanded ? 'Show less' : 'Read more';

      evaluateClamp(textEl, toggle);
    });

    window.addEventListener('resize', requestRecalc, { passive: true });

    requestRecalc();
  };

  const initAllTestimonialsCarousels = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initTestimonialsCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTestimonialsCarousels);
  } else {
    initAllTestimonialsCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector(ROOT_SELECTOR) || event.target;
    if (section?.matches?.(ROOT_SELECTOR)) {
      initTestimonialsCarousel(section);
    } else {
      event.target?.querySelectorAll?.(ROOT_SELECTOR)?.forEach?.(initTestimonialsCarousel);
    }
  });
})();